
import { API } from 'utils/api';
import { messageOf, statusOf } from 'utils/apiError';
import { STATUS_CONFLICT, STATUS_FORBIDDEN } from 'utils/constants';
import { Questionary, QuestionaryRepo, AlreadyEditingAssignmentError, RedirectData } from 'assignment/questionary/Questionary';
import { buildQuestionaryRequestDTO, buildQuestionary, buildQuestionaryView } from './factory';
import { TypedQuestion, Article, QuestionType, Subject, Grade } from 'assignment/Assignment';
import { QuestionDTO, TeacherAssignmentByIdResponseDTO } from '../api';
import { parseQueryString } from 'utils/queryString';

import { Notification, NotificationTypes } from 'components/common/Notification/Notification';
import intl from 'react-intl-universal';

const SETTIMEOUT = 500;

export interface RevisionDTO {
  id: number;
  title: string;
  description: string;
  author: string;
  authoravatar: string;
  questions: Array<QuestionDTO>;
  relatedArticles: Array<Article>;
  featuredImage?: string;
  backgroundImage?: string;
}

export interface RevisionByIdDTO {
  questions: Array<TypedQuestion>;
  subjects: Array<Subject>;
  grades: Array<Grade>;
  answers: Array<AnswerResponseDTO>;
}

export interface AnswerResponseDTO {
  questionId: number;
  type: QuestionType;
  payload: string | Array<{ optionId: number; title: string }>;
}

export interface QuestionaryRequestDTO {
  uuid: string;
  answerContent: Array<AnswerResponseDTO>;
  redirectData?: RedirectData;
}

export interface DraftQuestionaryRequestDTO {
  uuid: string;
  answerContent: Array<AnswerResponseDTO>;
}

export interface QuestionaryResponseDTO {
  id: number;
  uuid?: string;
  answers: Array<AnswerResponseDTO> | null;
  revision: RevisionDTO;
  redirectData?: RedirectData;
}

export interface QuestionaryByIdResponseDTO {
  revisionId: number;
  title: string;
  numberOfQuestions: number;
  revisionContent: RevisionByIdDTO;
}

export class QuestionaryApi implements QuestionaryRepo {

  public async setReadStatusArticle(idAssignment: number, revision: number, wpArticle: number, levelId: number, graduation: number) {
    await API.put(`api/student/assignments/${idAssignment}/article/mark`, {
      revision,
      wpArticle,
      graduation
      // levelId TODO rework it in future
    });
  }

  public async getNewQuestionaryByAssignmentId(id: number): Promise<Questionary> {
    try {
      const response = await API.get<QuestionaryResponseDTO>(`api/student/assignments/${id}/answer/create`);
      return buildQuestionary(response.data, id);
    } catch (error) {
      if (statusOf(error) === STATUS_FORBIDDEN) {
        Notification.create({
          type: NotificationTypes.ERROR,
          title: intl.get('assignment list.not for you')
        });
      }
      setTimeout(
        () => {
          window.location.href = '/assignments/';
        },
        SETTIMEOUT,
      );
      throw error;
    }
  }

  public async getNewQuestionaryByAssignmentIdFromTeachingPath(
    id: number,
    redirectData: RedirectData
    ): Promise<Questionary> {
    const response = await API.get<QuestionaryResponseDTO>(
      `api/student/teaching-paths/${redirectData.teachingPath}/node/${redirectData.node}/assignments/${id}/answer/create`, {
      });
    return buildQuestionary(response.data, id);
  }

  public async getAssignmentQuestionaryById(assignmentId: number):Promise<Questionary> {
    const search = parseQueryString(window.location.search).locale_id;
    const response = search ? await API.get<TeacherAssignmentByIdResponseDTO>(
      `api/teacher/assignments/${assignmentId}`, {
        params: {
          localeId: search,
          withBackground: true
        }
      }
    ) : await API.get<TeacherAssignmentByIdResponseDTO>(
      `api/teacher/assignments/${assignmentId}`, {
        params: {
          withBackground: true
        }
      }
    );

    return buildQuestionaryView(response.data);
  }

  public async getAssignmentQuestionaryByIdPreview(assignmentId: number):Promise<Questionary> {
    const search = parseQueryString(window.location.search).locale_id;
    const response = search ? await API.get<TeacherAssignmentByIdResponseDTO>(
      `api/teacher/assignments/${assignmentId}`, {
        params: {
          localeId: search,
          withBackground: true
        }
      }
    ) : await API.get<TeacherAssignmentByIdResponseDTO>(
      `api/teacher/assignments/${assignmentId}`, {
        params: {
          withBackground: true
        }
      }
    );

    return buildQuestionaryView(response.data);
  }

  public async saveQuestionary(questionary: Questionary, redirectData?: RedirectData): Promise<string> {
    try {
      const response = await API.put<{ updateAt: string }>(
        `api/student/assignments/${questionary.assignment.id}/answer/draft/${questionary.id}`,
        buildQuestionaryRequestDTO(questionary, redirectData)
      );

      return response.data.updateAt;
    } catch (error) {
      if (statusOf(error) === STATUS_CONFLICT) {
        throw new AlreadyEditingAssignmentError();
      }

      throw error;
    }
  }

  public async publishQuestionary(questionary: Questionary) {
    try {
      await API.post(`api/student/assignments/${questionary.assignment.id}/answer/${questionary.id}`, buildQuestionaryRequestDTO(questionary)
      );

    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: messageOf(error)
      });
    }
  }

  public async publishFromTeachingPath(questionary: Questionary, redirectData: RedirectData) {
    try {
      await API.post(`api/student/teaching-paths/${redirectData.teachingPath}/node/${redirectData.node}/assignments/${questionary.assignment.id}/answer/${questionary.id}`,
                     buildQuestionaryRequestDTO(questionary, redirectData)
      );
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: messageOf(error)
      });
    }
  }

  public async deleteQuestionary(questionary: Questionary) {
    await API.delete(`api/student/assignments/${questionary.assignment.id}/answer/${questionary.id}`);
  }

  public async revertQuestionary(questionary: Questionary) {
    const { data } = await API.get<QuestionaryResponseDTO>(`api/student/assignments/${questionary.assignment.id}/answer/${questionary.id}/revert`);
    return buildQuestionary(data, questionary.assignment.id);
  }
}
